<template>
	<div class="matchdays">
		<LoadingSpinner
			v-if="isLoading"
			class="matchdays__loading-spinner"
		/>
		<template v-if="!isLoading">
			<div class="matchdays__list-w">
				<div
					v-if="showScrollFilter"
					class="matchdays__month-selector-w"
				>
					<div class="matchdays__month-selector-inner-w">
						<div
							v-for="(listEntry, i) in groupedEvents"
							:key="listEntry.groupKey"
							ref="bucketSelect"
							:data-group-key="listEntry.groupKey"
							class="matchdays__month-select"
							:class="{'is-active': i === 0}"
							@click="handleBucketSelect(listEntry.groupKey)"
						>
							<span class="u-hide@md">{{ listEntry.scrollLabel.shortWithoutDay || listEntry.scrollLabel.short }}</span>
							<span class="u-show@md">{{ listEntry.scrollLabel.long }}</span>
						</div>
					</div>
				</div>
				<div class="matchdays__list">
					<div
						v-for="listEntry in groupedEvents"
						:key="listEntry.groupKey"
						class="matchdays__list-inner-w"
					>
						<div
							:id="listEntry.groupKey"
							ref="scrollAnchor"
							:data-group-key="listEntry.groupKey"
							class="matchdays__list-scroll-anchor"
						/>
						<h3 class="matchdays__list-title">
							{{ listEntry.titleMain.long || listEntry.titleMain }}
						</h3>
						<template v-if="listEntry.duelGroups.length > 0">
							<div
								v-for="duel in listEntry.duelGroups"
								:id="duel.scrollAnchor"
								:key="duel.scrollAnchor"
								class="matchdays__list-duel-w"
							>
								<div
									class="matchdays__list-duel-standing"
									:class="{'-sets-view': isSetsView}"
								>
									<div class="matchdays__list-duel-team -home">
										<span class="matchdays__list-duel-team-name">{{ duel.homeTeamName }}</span> <span class="u-hide@sm">{{ noSpoilerMode ? '-' : duel.overallScores.homeTeamScore }}</span>
									</div>
									<span class="matchdays__list-duel-standing-score u-show@sm">
										{{ noSpoilerMode ? '-' : duel.overallScores.homeTeamScore }} : {{ noSpoilerMode ? '-' : duel.overallScores.guestTeamScore }}
									</span>
									<div class="matchdays__list-duel-team -guest">
										<span class="matchdays__list-duel-team-name">{{ duel.guestTeamName }}</span> <span class="u-hide@sm">{{ noSpoilerMode ? '-' : duel.overallScores.guestTeamScore }}</span>
									</div>
								</div>
								<div
									v-for="match in duel.events"
									:key="match.id"
									class="matchdays__list-date-w -duel"
								>
									<h4 class="matchdays__list-date">
										{{ getDateTime(match.startTimestamp, { withTime: false, withWeekdayPrefix: true, weekdayFormatLong: true, monthFormatLong: true }) }}<br>
									</h4>
									<EventListItem
										:unix-now="sharedUnixNow"
										v-bind="EventListItemProps(match, { hideCompetitionStageInfo })"
									/>
								</div>
							</div>
						</template>
						<template v-else>
							<div
								v-for="dateGroup in listEntry.dateGroups"
								:key="dateGroup.key"
								class="matchdays__list-date-w"
							>
								<h4
									v-if="dateGroup.title"
									class="matchdays__list-date"
								>
									{{ dateGroup.title }}<br>
								</h4>
								<EventListItem
									v-for="match in dateGroup.matches"
									:key="match.id"
									:unix-now="sharedUnixNow"
									v-bind="EventListItemProps(match, { hideCompetitionStageInfo })"
								/>
							</div>
						</template>
					</div>
					<EmptyState
						v-if="groupedEvents.length === 0"
						:text="$I18n.trans('commons.no_types_found', { type: $I18n.trans_choice('commons.event', 2) })"
					/>
				</div>
			</div>
		</template>
	</div>
</template>

<script>
import EventListItem from '@js/components/EventCenter/EventListItem/EventListItem.vue';
import LoadingSpinner from '@shared/components/LoadingSpinner';
import EmptyState from '@js/components/EmptyState';
import { EventListItemProps } from '@js/components/EventCenter/EventListItem/EventListItemProps';
import { getDateTime } from '@shared/utils/timeUtils';
import { ParticipantType } from '@shared/enums/ParticipantType';
import { SportName } from '@shared/enums/SportName';
import { mapState } from 'vuex';
import { EVENT_CENTER_VIEWS } from './eventCenterState';

const GROUPING_MODES = Object.freeze({
	DAILY:    'daily',
	MONTHLY:  'monthly',
	SUBSTAGE: 'substage',
});

const DYNAMIC_GROUPING_THRESHOLD_DAYS = 28;
const CLOCK_UPDATE_INTERVAL_MS = 5 * 1000;
const DATE_LABEL_OPTIONS = {
	withTime:          false,
	withWeekdayPrefix: true,
	weekdayFormatLong: true,
	monthFormatLong:   true,
};
const DAY_IN_SECONDS = 24 * 60 * 60;

const groupEventsBy = (events, getKey, createGroup, appendToGroup) => {
	const groups = new Map();

	events.forEach(event => {
		const key = getKey(event);
		if (!groups.has(key)) {
			groups.set(key, createGroup(event, key));
		}
		appendToGroup(groups.get(key), event);
	});

	return Array.from(groups.values());
};

export default {
	name:       'EventCenterEventList',
	components: { LoadingSpinner, EventListItem, EmptyState },
	props:      {
		isLoading: {
			type:    Boolean,
			default: false,
		},
		events: {
			type:    Array,
			default: null,
		},
		viewMode: {
			type:     String,
			required: true,
		},
		selectedStage: {
			type:    Object,
			default: null,
		},
		selectedSubStageKey: {
			type:    [String, null],
			default: null,
		},
	},
	data() {
		return {
			EventListItemProps,
			internalEvents:            this.events || [],
			hasAutoScrolledOnPageLoad: false,
			scrollObserverActive:      false,
			scrollHandler:             null,
			clockTimerId:              null,
			sharedUnixNow:             Math.round(new Date().getTime() / 1000),
			getDateTime,
		};
	},
	computed: {
		...mapState({
			preferences: state => state.user.preferences,
		}),
		noSpoilerMode() {
			return this.preferences?.noSpoilerMode || false;
		},
		isCompetitionView() {
			return this.viewMode.startsWith('competition-');
		},
		hideCompetitionStageInfo() {
			return this.viewMode === 'competition-stage';
		},
		groupingMode() {
			if (this.viewMode === EVENT_CENTER_VIEWS.CURRENT) {
				return GROUPING_MODES.DAILY;
			}

			if ([EVENT_CENTER_VIEWS.UPCOMING, EVENT_CENTER_VIEWS.REPLAYS].includes(this.viewMode)) {
				return GROUPING_MODES.MONTHLY;
			}

			if (this.selectedStageHasSubStages) {
				return GROUPING_MODES.SUBSTAGE;
			}

			return this.resolveDynamicGroupingMode(this.internalEvents);
		},
		groupedEvents() {
			if (!this.internalEvents.length) {
				return [];
			}

			const groupingMode = this.groupingMode;
			const topLevelGroups = groupEventsBy(
				this.internalEvents,
				event => this.getTopLevelGroupMeta(event, groupingMode).key,
				event => {
					const meta = this.getTopLevelGroupMeta(event, groupingMode);

					return {
						groupKey:    meta.key,
						titleMain:   meta.title,
						scrollLabel: meta.scrollLabel,
						duelEnabled: meta.duelEnabled,
						dateGroups:  [],
					};
				},
				(group, event) => {
					group.dateGroups.push(event);
				}
			);

			return topLevelGroups.map(group => {
				const duelGroups = this.shouldGroupByDuel(group.dateGroups, group.duelEnabled)
					? this.groupDuels(group.dateGroups)
					: [];

				return {
					...group,
					duelGroups,
					dateGroups: duelGroups.length > 0 ? [] : this.buildDateGroups(group.dateGroups, groupingMode),
				};
			});
		},
		selectedStageHasSubStages() {
			return Array.isArray(this.selectedStage?.subStages) && this.selectedStage.subStages.length > 0;
		},
		showScrollFilter() {
			return this.viewMode === 'competition-chronological' && this.groupedEvents.length >= 2;
		},
		isSetsView() {
			return this.internalEvents[0]?.sport?.name === SportName.VOLLEYBALL || false;
		},
	},
	watch: {
		events(newValues) {
			this.internalEvents = Array.isArray(newValues) ? newValues : [];
			this.scheduleClockUpdates();
			this.$nextTick(() => {
				this.syncScrollFilterState({ shouldAutoScroll: true });
			});
		},
		viewMode() {
			this.$nextTick(() => {
				this.syncScrollFilterState();
			});
		},
		selectedStage() {
			this.$nextTick(() => {
				this.syncScrollFilterState({ shouldAutoScroll: true });
			});
		},
		selectedSubStageKey() {
			this.$nextTick(() => {
				this.syncScrollFilterState({ shouldAutoScroll: true });
			});
		},
	},
	mounted() {
		this.scheduleClockUpdates();
		this.$nextTick(() => {
			this.syncScrollFilterState({ shouldAutoScroll: true });
		});
	},
	beforeDestroy() {
		this.clearClockTimer();
		this.destroyScrollObserver();
	},
	methods: {
		clearClockTimer() {
			if (this.clockTimerId) {
				window.clearTimeout(this.clockTimerId);
				window.clearInterval(this.clockTimerId);
				this.clockTimerId = null;
			}
		},
		hasEventsWithDynamicStatus() {
			return this.internalEvents.some(event => Number(event?.endTimestamp || 0) > this.sharedUnixNow);
		},
		scheduleClockUpdates() {
			this.clearClockTimer();
			this.sharedUnixNow = Math.round(new Date().getTime() / 1000);

			if (!this.hasEventsWithDynamicStatus()) {
				return;
			}

			// Matchday pages often contain only past events; avoid hundreds of idle item timers
			// and only keep a shared clock while at least one event can still move from
			// upcoming -> live -> ended. A short shared tick keeps live badges responsive
			// without forcing the full list to recompute every second.
			this.clockTimerId = window.setInterval(() => {
				this.sharedUnixNow = Math.round(new Date().getTime() / 1000);

				if (!this.hasEventsWithDynamicStatus()) {
					this.clearClockTimer();
				}
			}, CLOCK_UPDATE_INTERVAL_MS);
		},
		resolveDynamicGroupingMode(events) {
			if (!events.length) {
				return GROUPING_MODES.DAILY;
			}

			const sortedEvents = [...events].sort((a, b) => a.startTimestamp - b.startTimestamp);
			const spreadInDays = Math.floor((sortedEvents[sortedEvents.length - 1].startTimestamp - sortedEvents[0].startTimestamp) / DAY_IN_SECONDS);
			return spreadInDays <= DYNAMIC_GROUPING_THRESHOLD_DAYS ? GROUPING_MODES.DAILY : GROUPING_MODES.MONTHLY;
		},
		getTopLevelGroupMeta(event, groupingMode) {
			if (groupingMode === GROUPING_MODES.MONTHLY) {
				const monthMoment = moment.unix(event.startTimestamp);
				const monthKey = monthMoment.format('YYYY-MM');

				return {
					key:   `month_${monthKey}`,
					title: {
						long:  this.$Helpers.ucfirst(monthMoment.format('MMMM YYYY')),
						short: monthMoment.format('MMM').replace('.', '').toUpperCase(),
					},
					scrollLabel: {
						long:  this.$Helpers.ucfirst(monthMoment.format('MMMM')),
						short: monthMoment.format('MMM').replace('.', '').toUpperCase(),
					},
					duelEnabled: false,
				};
			}

			if (groupingMode === GROUPING_MODES.SUBSTAGE) {
				const subStageKey = this.resolveEventSubStageKey(event);
				const subStage = this.selectedStage?.subStages?.find(stage => stage.key === subStageKey) || null;
				const fallbackLabel = this.resolveStageLabel(this.selectedStage);
				const subStageLabel = this.resolveStageLabel(subStage) || fallbackLabel;

				// Parent-stage assigned events stay visible in "All Sub-Stages" mode by using
				// the selected stage itself as a synthetic bucket when no sub-stage key exists.
				return {
					key:   `substage_${subStageKey || 'stage-root'}`,
					title: {
						long:  subStageLabel,
						short: subStageLabel,
					},
					scrollLabel: {
						long:  subStageLabel,
						short: subStageLabel,
					},
					duelEnabled: Boolean(subStage?.duel),
				};
			}

			const dayMoment = moment.unix(event.startTimestamp);
			const dateLabelLong = getDateTime(event.startTimestamp, DATE_LABEL_OPTIONS);
			const dateLabelShort = dayMoment.format('ddd DD.MM');
			const dateLabelShortWithoutDay = dayMoment.format('DD.MM');

			return {
				key:   `day_${dayMoment.format('YYYY-MM-DD')}`,
				title: {
					long:  dateLabelLong,
					short: dateLabelShort,
				},
				scrollLabel: {
					long:            dateLabelLong,
					short:           dateLabelShort,
					shortWithoutDay: dateLabelShortWithoutDay,
				},
				duelEnabled: false,
			};
		},
		buildDateGroups(events, groupingMode) {
			const shouldShowDateTitle = groupingMode !== GROUPING_MODES.DAILY;

			return groupEventsBy(
				events,
				event => moment.unix(event.startTimestamp).format('YYYY-MM-DD'),
				event => ({
					key:     `date_${moment.unix(event.startTimestamp).format('YYYY-MM-DD')}`,
					title:   shouldShowDateTitle ? getDateTime(event.startTimestamp, DATE_LABEL_OPTIONS) : null,
					matches: [],
				}),
				(group, event) => {
					group.matches.push(event);
				}
			);
		},
		resolveStageLabel(stage) {
			if (!stage) {
				return '';
			}

			return stage.label || this.$Helpers.getTranslatedContent(stage.name) || stage.key || '';
		},
		resolveEventSubStageKey(event) {
			return event.subStageKey ?? null;
		},
		shouldGroupByDuel(events, duelEnabled = false) {
			if (!duelEnabled || this.viewMode !== 'competition-stage' || events.length <= 1) {
				return false;
			}

			const duelGroups = this.groupDuels(events);
			return duelGroups.some(group => group.events.length > 1);
		},
		getCurrentGroupKey() {
			if (!this.groupedEvents.length) {
				return null;
			}

			if (!this.showScrollFilter) {
				return this.groupedEvents[0].groupKey;
			}

			const currentMonth = moment().month();
			const currentYear = moment().year();
			const currentGroup = this.groupedEvents.find(group => {
				const firstMatch = group.dateGroups[0]?.matches[0];
				if (!firstMatch) {
					return false;
				}

				const matchMoment = moment.unix(firstMatch.startTimestamp);
				if (this.groupingMode === GROUPING_MODES.MONTHLY) {
					return matchMoment.year() === currentYear && matchMoment.month() === currentMonth;
				}

				return matchMoment.isSame(moment(), 'day');
			});

			return currentGroup?.groupKey || this.groupedEvents[0].groupKey;
		},
		handleBucketSelect(id) {
			document.querySelector(`#${id}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
		},
		// Preserve the existing parent hook when returning from table/top-scorer.
		setViewToMatchdays() {
			this.$nextTick(() => {
				this.syncScrollFilterState();
			});
		},
		initScrollObserver() {
			if (this.scrollObserverActive) {
				return;
			}

			let ticking = false;
			this.scrollHandler = () => {
				if (!ticking) {
					window.requestAnimationFrame(() => {
						this.setScrollFilterSelection();
						ticking = false;
					});
					ticking = true;
				}
			};

			window.addEventListener('scroll', this.scrollHandler);
			this.scrollObserverActive = true;
		},
		destroyScrollObserver() {
			if (this.scrollHandler) {
				window.removeEventListener('scroll', this.scrollHandler);
				this.scrollHandler = null;
				this.scrollObserverActive = false;
			}
		},
		syncScrollFilterState({ shouldAutoScroll = false } = {}) {
			if (!this.showScrollFilter) {
				this.destroyScrollObserver();
				return;
			}

			this.initScrollObserver();
			this.setScrollFilterSelection();

			// Keep the initial jump deterministic: once the page has aligned the list
			// to the relevant top-level bucket, later view toggles should preserve the user's position.
			if (!shouldAutoScroll || this.hasAutoScrolledOnPageLoad) {
				return;
			}

			const currentGroupKey = this.getCurrentGroupKey();
			if (currentGroupKey) {
				this.handleBucketSelect(currentGroupKey);
			}
			this.hasAutoScrolledOnPageLoad = true;
		},
		setScrollFilterSelection() {
			const scrollAnchor = this.$refs.scrollAnchor;
			if (!scrollAnchor || scrollAnchor.length === 0 || !this.$refs.bucketSelect) {
				return;
			}

			const anchorItems = scrollAnchor.map(anchor => {
				return {
					el:  anchor,
					pos: anchor.getBoundingClientRect().top - 10,
				};
			});

			if (anchorItems.length === 0) {
				return;
			}

			const currentGroupKey = anchorItems.filter(item => item.pos <= 0).reverse()[0]?.el.dataset.groupKey || anchorItems[0].el.dataset.groupKey;

			this.$refs.bucketSelect.forEach(select => {
				select.classList.remove('is-active');
				if (select.dataset.groupKey === currentGroupKey) {
					select.classList.add('is-active');
				}
			});
		},
		groupDuels(events) {
			const groups = [];
			events.forEach(event => {
				const groupIndex = groups.findIndex(group => {
					return group.events.some(groupEvent => {
						const groupEventParticipantIds = new Set(groupEvent.participants.map(participant => participant.participatable.id));
						const eventParticipantIds = new Set(event.participants.map(participant => participant.participatable.id));

						return [...groupEventParticipantIds].every(id => eventParticipantIds.has(id));
					});
				});
				if (groupIndex === -1) {
					groups.push({
						events: [event],
					});
				} else {
					groups[groupIndex].events.push(event);
				}
			});

			// The renderer only groups duels inside an already-selected Event Center scope,
			// so same-participant pairing stays local and does not create cross-view coupling.
			return groups.map(group => {
				const home = group.events[0].participants?.find(participant => participant.type === ParticipantType.HOME)?.participatable;
				const guest = group.events[0].participants?.find(participant => participant.type === ParticipantType.GUEST)?.participatable;
				const homeClubName = home?.club?.name || home?.name || '-';
				const guestClubName = guest?.club?.name || guest?.name || '-';
				const homeId = home?.id || 'home';
				const guestId = guest?.id || 'guest';

				return {
					...group,
					events:        [...group.events].sort((a, b) => a.startTimestamp - b.startTimestamp),
					homeTeamName:  homeClubName,
					guestTeamName: guestClubName,
					teamIds:       [homeId, guestId],
					scrollAnchor:  `duel_${homeId}_${guestId}`,
					overallScores: group.events.reduce((acc, current) => {
						const isEventHomeTeamOverallHomeTeam = current.participants?.find(participant => participant.type === ParticipantType.HOME)?.participatable?.id === homeId;
						const homeTeamScore = this.getEventSeriesScore(current, ParticipantType.HOME);
						const guestTeamScore = this.getEventSeriesScore(current, ParticipantType.GUEST);

						// If final score is not set or match is live, do not count the match
						if (homeTeamScore === null || guestTeamScore === null || current.isLive) {
							return acc;
						}

						const eventHomeTeamWon = homeTeamScore > guestTeamScore;
						const duelHomeTeamWon = (eventHomeTeamWon && isEventHomeTeamOverallHomeTeam) || (!eventHomeTeamWon && !isEventHomeTeamOverallHomeTeam);

						return {
							homeTeamScore:  duelHomeTeamWon ? acc.homeTeamScore + 1 : acc.homeTeamScore,
							guestTeamScore: !duelHomeTeamWon ? acc.guestTeamScore + 1 : acc.guestTeamScore,
						};
					}, { homeTeamScore: 0, guestTeamScore: 0 }),
				};
			});
		},
		getEventSeriesScore(event, participantType) {
			const latestScore = event.scores?.[event.scores.length - 1];
			const periodsWon = participantType === ParticipantType.HOME
				? latestScore?.teamHomePeriodsWon
				: latestScore?.teamGuestPeriodsWon;

			// Volleyball finalScore can be total rally points in Event Center payloads.
			// Prefer cumulative periods won from the last score row to derive duel winners.
			if (Number.isInteger(periodsWon)) {
				return periodsWon;
			}

			return participantType === ParticipantType.HOME
				? event.finalScore?.teamHomeScore ?? null
				: event.finalScore?.teamGuestScore ?? null;
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.matchdays {
	position: relative;
}

.matchdays__loading-spinner {
	position: absolute;
	top: 100px;
	left: 50%;
	transform: translate(-50%);
}

.matchdays__list-w {
	display: flex;
}

.matchdays__month-selector-w {
	min-width: 65px;

	@include size(md-up) {
		min-width: 140px;
	}
}

.matchdays__month-selector-inner-w {
	position: sticky;
	top: 110px;
}

.matchdays__month-select {
	color: var(--color-grey-400);
	padding: $sp2 0;
	margin-bottom: $sp3;
	cursor: pointer;

	&:focus,
	&:hover,
	&.is-active {
		color: var(--color-soft-white);

		body.-t-light & { // stylelint-disable-line selector-no-qualifying-type
			color: var(--color-hype-yellow);
		}
	}
}

.matchdays__list {
	flex-grow: 1;

	body.-t-light & { // stylelint-disable-line selector-no-qualifying-type
		color: var(--color-black);
	}
}

.matchdays__list-inner-w {
	&:not(:last-child) {
		margin-bottom: $sp6;
	}
}

.matchdays__list-scroll-anchor {
	visibility: hidden;
	width: 0;
	height: 0;
	position: relative;
	top: -100px;
}

.matchdays__list-title {
	@include font(primary, book, normal, fs-170);
	position: relative;
	margin-bottom: $sp3;

	@include size(md-up) {
		@include font(primary, light, normal, fs-170);
		margin-bottom: $sp4;
	}
}

.matchdays__list-date-w {
	&:not(:last-child) {
		margin-bottom: $sp6;

		&.-duel {
			margin-bottom: $sp4;
		}
	}
}

.matchdays__list-date {
	@include font(primary, medium, normal, fs-100);
	margin-bottom: $sp2;

	.matchdays__list-date-w.-duel & {
		margin-bottom: 0;
	}

	@include size(md-up) {
		margin-bottom: $sp3;
	}
}

// Duel title styles
.matchdays__list-duel-w {
	padding-bottom: $sp4;

	@include size(md-up) {
		padding-bottom: $sp7;
	}
}

.matchdays__list-duel-standing {
	@include font(primary, medium, normal, fs-100);
	display: flex;
	justify-content: center;
	flex-direction: column;
	align-items: flex-start;
	padding-top: $sp3-5;
	padding-bottom: $sp3-5;
	margin-top: $sp4;
	margin-bottom: $sp4;
	border-top: 1px solid var(--color-grey-900);
	border-bottom: 1px solid var(--color-grey-900);
	gap: $sp3;

	&.-sets-view {
		@include size(sm-up) {
			margin-left: 0;
			justify-content: flex-start;
			padding-left: 0;
		}
	}

	@include size(sm-up) {
		@include font(primary, medium, normal, fs-130);
		flex-direction: row;
		align-items: center;
		gap: $sp6;
		margin-top: 0;
		border-top: none;
	}
	@include size(sm-mp) {
		@include font(primary, medium, normal, fs-150);
	}
}

.matchdays__list-duel-team {
	$result-width: 60px;
	$result-width-md: 110px;

	display: flex;
	align-items: center;
	gap: $sp3;
	width: 100%;
	justify-content: space-between;

	.matchdays__list-duel-standing.-sets-view & {
		@include size(sm-up) {
			width: auto;
		}
		@include size(md-up) {
			width: auto;
		}
	}

	// &.-guest {}

	@include size(sm-up) {
		width: calc(100% / 2 - #{$result-width});

		&.-home {
			justify-content: flex-end;
		}
	}

	@include size(md-up) {
		width: calc(100% / 2 - #{$result-width-md});
	}
}

.matchdays__list-duel-team-name {
	@include size(sm-up) {
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
		max-width: calc(50vw - 50px);
	}
}

.matchdays__list-duel-standing-score {
	padding: $sp3 $sp4;
	background-color: var(--color-grey-900);
	border-radius: $border-radius--sm;
	white-space: nowrap;
}

.matchdays__navigate-matchday {
	display: flex;
	justify-content: center;
	gap: $sp6;
	margin-bottom: $sp7;
}
</style>
